/** @jsx jsx */
import React, { useState } from 'react';
import { Box, Flex, jsx } from 'theme-ui';
import Slider from 'react-slick';
import { ImageFullWidth } from '../../components';
import useElementOnScreen from '../../hooks/useElementOnScreen';

import RenderNextPrevCaptionIcons from './RenderNextPrevIcons';
import RenderSlideCount from './RenderSlideCount';
import RenderTextBlock from './RenderTextBlock';

const carouselSingleWrap = {
  py: [3, null, 9],
};

const carouselStyles = {
  base: {},
  wrap: {
    maxWidth: ['90%', null, '85%'],
    py: [3, null, 0],
    ml: ['auto', null, 0],
    rightAlign: {
      ml: 'auto',
    },
  },
  imageStyles: {
    height: [220, 475, 828],
    transition: 'transform 2s ease',
    transform: 'translateX(0)',
  },
  sliderBody: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    pb: [2, null, 4],
    pr: [2, null, 0],
    leftAlign: {
      flexDirection: [null, null, 'row-reverse'],
    },
  },
  sliderAction: {
    variant: 'styles.flexCenter',
    display: ['none', null, 'flex'],
    mx: [4, 4, 7],
    flexShrink: 0,
  },
};

const sliderStyles = {
  pt: [null, null, 6],
  pb: [4, null, 5],
  '.slick-dots': {
    textAlign: 'right',
    pr: 1,
    li: {
      width: 'auto',
      height: 'auto',
      m: '4px',
      button: {
        width: '8px',
        height: '8px',
        bg: 'accent',
        borderRadius: '50%',
        ':before': {
          display: 'none',
        },
      },
    },
    '.slick-active': {
      button: {
        bg: 'backgroundInverted',
        opacity: 0.3,
      },
    },
  },
  '.slick-slide': {
    overflow: 'hidden',
  },
  '.animating-right .gatsby-image-wrapper': {
    transform: [null, null, 'translateX(15%)'],
  },
  '.animating-left .gatsby-image-wrapper': {
    transform: [null, null, 'translateX(-15%)'],
  },
};

export const getLayoutStyle = (style) => {
  switch (style) {
    case 'right-image-align':
      return {
        base: carouselStyles.base.rightAlign,
        wrap: carouselStyles.wrap.rightAlign,
        sliderBody: carouselStyles.sliderBody.rightAlign,
      };
    case 'left-image-align':
      return {
        base: carouselStyles.base.leftAlign,
        wrap: carouselStyles.wrap.leftAlign,
        sliderBody: carouselStyles.sliderBody.leftAlign,
      };
    default:
      return {
        base: carouselStyles.base.rightAlign,
        wrap: carouselStyles.wrap.rightAlign,
        sliderBody: carouselStyles.sliderBody.rightAlign,
      };
  }
};

const RenderCarousel = ({ props: { bilateralCarousel, carouselStyle, title, bilateralTextBlock } }) => {
  const [sliderRef, setsliderRef] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [SliderImageRef, isSliderImageVisible] = useElementOnScreen();
  const [SliderActionsRef, isSliderActionsVisible] = useElementOnScreen();

  const handleSlideAfterChange = (index) => {
    // Remove Class from all elements of slick items
    const SlideElement = sliderRef.innerSlider.list.querySelectorAll(`.slick-slide`);
    [].forEach.call(SlideElement, (el) => {
      el.classList.remove('animating-left', 'animating-right');
    });

    setCurrentSlide(index);
  };

  const handleSlideBeforeChange = (prev, next) => {
    const getNextSlide = prev - next;
    const getSlidesCount = sliderRef.innerSlider.state.slideCount;

    const prevSlideElement = sliderRef.innerSlider.list.querySelectorAll(`.slick-slide[data-index="${prev}"]`);
    // wait for slider to update its DOM
    const beforeChangeTimer = setTimeout(() => {
      // do magic to get slider direction
      if (getNextSlide === 1 || getNextSlide === (getSlidesCount - 1) * -1) {
        prevSlideElement[0].classList.add('animating-left');
      } else {
        prevSlideElement[0].classList.add('animating-right');
      }
    }, 10);

    return () => clearTimeout(beforeChangeTimer);
  };

  const settings = {
    speed: 2000,
    autoplay: false, //bilateralCarousel.length > 1,
    dots: false,
    arrows: false,
    infinite: true,
    autoplaySpeed: 5000,
    beforeChange: handleSlideBeforeChange,
    afterChange: handleSlideAfterChange,
    responsive: [
      {
        breakpoint: 1023,
        settings: {
          dots: true,
        },
      },
    ],
    adaptiveHeight: true,
  };
  return (
    <Box key={title} sx={{ ...carouselStyles.base, ...getLayoutStyle(carouselStyle).base }}>
      <Box sx={{ ...carouselStyles.wrap, ...getLayoutStyle(carouselStyle).wrap }}>
        <Box ref={SliderImageRef} sx={{ ...sliderStyles, ...{ transition: 'opacity 1s ease', opacity: isSliderImageVisible ? 1 : 0 } }}>
          <Slider ref={(c) => setsliderRef(c)} {...settings}>
            {bilateralCarousel.map((imgObj) => (
              <Box key={imgObj._key}>
                <ImageFullWidth style={carouselStyles.imageStyles} key={imgObj._key} src={imgObj.asset._ref} alt={imgObj.asset._ref} />
              </Box>
            ))}
          </Slider>
        </Box>
        <Flex sx={{ ...carouselStyles.sliderBody, ...getLayoutStyle(carouselStyle).sliderBody }}>
          {bilateralTextBlock && <RenderTextBlock bilateralTextBlock={bilateralTextBlock} carouselStyles={carouselStyles} />}
          {bilateralCarousel.length > 1 && (
            <Box
              ref={SliderActionsRef}
              sx={{ ...carouselStyles.sliderAction, ...{ transition: 'opacity 1s ease 0.25s', opacity: isSliderActionsVisible ? 1 : 0 } }}
            >
              <RenderSlideCount currentSlide={currentSlide} total={bilateralCarousel.length} />
              {sliderRef && <RenderNextPrevCaptionIcons sliderRef={sliderRef} />}
            </Box>
          )}
        </Flex>
      </Box>
    </Box>
  );
};

const Carousel = ({ isList, ...restProps }) => {
  return (
    <>
      {isList ? (
        <RenderCarousel props={{ ...restProps }} />
      ) : (
        <Box sx={carouselSingleWrap}>
          <RenderCarousel props={{ ...restProps }} />
        </Box>
      )}
    </>
  );
};

export default Carousel;
